import { routeParameter } from "./route-parameter.ts";

export type MatchedParams = Record<string, string | readonly string[]>;

function parameterValue({
  part,
  parameter: { prefix, suffix },
}: {
  readonly part: string;
  readonly parameter: { readonly prefix: string; readonly suffix: string };
}): string | null {
  if (!part.startsWith(prefix) || !part.endsWith(suffix)) return null;

  return part.slice(prefix.length, suffix ? -suffix.length : undefined) || null;
}

function matchRequiredSegment({
  segment,
  rest,
  parts,
}: {
  readonly segment: string;
  readonly rest: readonly string[];
  readonly parts: readonly string[];
}): MatchedParams | null {
  const [part, ...remaining] = parts;

  if (part === undefined) return null;

  const parameter = routeParameter(segment);
  const value = parameter ? parameterValue({ part, parameter }) : part;

  if (value === null || (!parameter && segment !== part)) return null;

  const tail = matchPattern({ pattern: rest, parts: remaining });

  if (!tail) return null;

  if (!parameter) return tail;

  return { [parameter.key]: value, ...tail };
}

function matchWildcard({
  parts,
  parameter,
}: {
  readonly parts: readonly string[];
  readonly parameter: NonNullable<ReturnType<typeof routeParameter>>;
}): MatchedParams | null {
  const lastPart = parts.at(-1);

  if (lastPart === undefined) return parameter.optional ? {} : null;

  const last = parameterValue({ part: lastPart, parameter });

  if (last === null) return null;

  return { [parameter.key]: [...parts.slice(0, -1), last] };
}

export function matchPattern({
  pattern,
  parts,
}: {
  readonly pattern: readonly string[];
  readonly parts: readonly string[];
}): MatchedParams | null {
  const [segment, ...rest] = pattern;

  if (!segment) return parts.length === 0 ? {} : null;

  const parameter = routeParameter(segment);

  if (parameter?.wildcard) return matchWildcard({ parts, parameter });

  if (parameter?.optional) {
    const without = matchPattern({ pattern: rest, parts });

    if (without) return without;
  }

  return matchRequiredSegment({ segment, rest, parts });
}
